import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
} from '@accure/ui';
import { Listbox, Menu } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Filter, Search } from 'lucide-react';
import { z } from 'zod';

import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/communication/components/ui/radio-group';
import { useMe } from '@/hooks/use-me';
import { plural, singular } from '@/utils/format/label';

import { useCurrentSite } from '../hooks/use-current-site';
import { useDetailsId } from '../hooks/use-details-id';
import {
  filtersEmptyState,
  type ListFilters,
  useListFilters,
} from '../hooks/use-list-filters';

const FiltersFormSchema = z.object({
  search: z.string(),
  type: z.enum(['all', 'email', 'meeting']),
  siteIds: z.array(z.string()),
});
export type FiltersForm = z.infer<typeof FiltersFormSchema>;

const typeOptions: Record<FiltersForm['type'], string> = {
  all: 'All',
  email: 'Email',
  meeting: 'Meeting',
};

function formValueFromFilters(filters: ListFilters): FiltersForm {
  return {
    search: filters.search ?? '',
    type: filters.type ?? 'all',
    siteIds: filters.siteIds ?? [],
  };
}

export function CommunicationSidebarFilters() {
  const me = useMe();
  const [filters, history, filtersStatus] = useListFilters();
  const [, setDetailsId] = useDetailsId();
  const sites = GetAllSites.useQuery();
  const site = useCurrentSite();

  const form = useForm<FiltersForm>({
    resolver: zodResolver(FiltersFormSchema),
    defaultValues: formValueFromFilters(filters),
  });

  function onSubmit(values: FiltersForm) {
    const filters: ListFilters = {
      search: values.search || undefined,
      type: values.type === 'all' ? undefined : values.type,
      siteIds: values.siteIds.length > 0 ? values.siteIds : undefined,
    };
    history.replace(filters);
    form.reset(formValueFromFilters(filters));
    setDetailsId(undefined);
  }

  function handleReset() {
    history.replace(filtersEmptyState);
    form.reset(formValueFromFilters(filtersEmptyState));
    setDetailsId(undefined);
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="rounded-full p-2 text-gray-600 hover:bg-gray-100">
        <Filter className="size-5" />
      </Menu.Button>
      <Menu.Items className="absolute right-0 z-[100] mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
        <div className="px-4 py-3">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Filters</h3>
                {filtersStatus.active ? (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleReset}
                    className="h-auto p-0 text-primary-600 hover:underline"
                  >
                    Reset
                  </Button>
                ) : null}
              </div>

              <FormField
                control={form.control}
                name="search"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-900">
                      Search
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 size-4 text-gray-400" />
                        <Input
                          placeholder="Search communications..."
                          className="pl-8"
                          {...field}
                          onKeyDown={function (e) {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              onSubmit({
                                ...form.getValues(),
                                search: e.currentTarget?.value,
                              });
                            }
                          }}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              {!site ? (
                <FormField
                  control={form.control}
                  name="siteIds"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-sm font-medium text-gray-900">
                        Filter by {singular(me.config.group.labels.name)}
                      </FormLabel>
                      <Listbox
                        as="div"
                        className="relative"
                        value={field.value}
                        onChange={(newValue) => field.onChange(newValue)}
                        multiple
                      >
                        <Listbox.Button className="relative flex w-full cursor-default items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                          <span className="block truncate text-left">
                            {field.value && field.value.length > 0 ? (
                              field.value.length === 1 ? (
                                (sites.data?.find(
                                  (site) => site.id === field.value[0],
                                )?.name ?? field.value[0])
                              ) : (
                                `${field.value.length} ${plural(me.config.group.labels.name).toLowerCase()} selected`
                              )
                            ) : (
                              <span className="text-muted-foreground">
                                Select{' '}
                                {plural(
                                  me.config.group.labels.name,
                                ).toLowerCase()}
                                ...
                              </span>
                            )}
                          </span>
                          <span className="pointer-events-none flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="opacity-50"
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </span>
                        </Listbox.Button>
                        <Listbox.Options className="absolute top-full z-50 my-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                          {sites.data?.map((site) => (
                            <Listbox.Option
                              key={site.id}
                              value={site.id}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  active
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-900'
                                }`
                              }
                            >
                              {({ selected }) => (
                                <>
                                  <span
                                    className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                  >
                                    {site.name}
                                  </span>
                                  {selected ? (
                                    <span className="text-primary absolute inset-y-0 left-0 flex items-center pl-3">
                                      <Check
                                        className="size-4"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          )) ?? null}
                          {field.value && field.value.length > 0 && (
                            <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2">
                              <span className="text-xs text-gray-500">
                                {field.value.length} selected
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  field.onChange([]);
                                }}
                              >
                                Clear
                              </Button>
                            </div>
                          )}
                        </Listbox.Options>
                      </Listbox>
                    </FormItem>
                  )}
                />
              ) : null}

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="text-sm font-medium text-gray-900">
                      Filter by type
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-col space-y-1"
                      >
                        {Object.entries(typeOptions).map(([type, label]) => (
                          <FormItem
                            className="flex items-center space-x-3 space-y-0"
                            key={type}
                          >
                            <FormControl>
                              <RadioGroupItem value={type} />
                            </FormControl>
                            <FormLabel className="font-normal">
                              {label}
                            </FormLabel>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={!form.formState.isDirty}
              >
                Apply Filter
              </Button>
            </form>
          </Form>
        </div>
      </Menu.Items>
    </Menu>
  );
}
